/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { createContext, FC, useEffect, useState } from "react";
import { IRuntimeError, RuntimeContext } from "@adapptio/lib-blueprint";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	TSimpleEventEmitter
} from "@adapptio/lib-shared";

/**
 * Model Runtime Context
 * Provides interface to access model runtime information.
 */
export interface IModelRuntimeContext {
	/** Emitted when runtime has rendered spec */
	onRender: TSimpleEventEmitter<void>;
	/** Returns runtime error for given model node ID */
	modelRuntimeErrors: Map<number, IRuntimeError[]>;
}

export interface IModelRuntimeContextProviderProps {
	rCtx: RuntimeContext;
}

export const ModelRuntimeContext = createContext<IModelRuntimeContext>(null);

/**
 * Component to create and provide model runtime context
 *
 * @param props Component props
 */
export const ModelRuntimeContextProvider: FC<IModelRuntimeContextProviderProps> = ({ rCtx, children }) => {
	const [ contextValue ] = useState<IModelRuntimeContext>(() => {
		return {
			onRender: createEventEmitter<void>(),
			modelRuntimeErrors: new Map()
		};
	});

	useEffect(() => {
		const handleRender = () => {
			contextValue.modelRuntimeErrors = new Map<number, IRuntimeError[]>();

			const errors = rCtx.getRuntimeErrors();

			for (let i = 0; i < errors.length; i++) {
				const err = errors[i];

				if (err.modelNodeId >= 0) {
					if (!contextValue.modelRuntimeErrors.has(err.modelNodeId)) {
						contextValue.modelRuntimeErrors.set(err.modelNodeId, []);
					}

					contextValue.modelRuntimeErrors.get(err.modelNodeId).push(err);
				}
			}

			emitEvent(contextValue.onRender);
		};

		onEvent(rCtx.renderEvent, handleRender);

		return () => {
			offEvent(rCtx.renderEvent, handleRender);
		};
	}, [ rCtx, contextValue ]);

	return <ModelRuntimeContext.Provider value={contextValue}>{children}</ModelRuntimeContext.Provider>;
};
